import { Redirect, Route, Switch } from 'react-router';
import { Helmet } from 'react-helmet';
import { User } from 'shared/state/misc/oidc';
import PageContext from 'shared/util/PageContext';
import SitePage from 'shared/components/SitePage';
import SiteBody from 'shared/components/SiteBody';
import SiteFooter from 'shared/components/SiteFooter';
import PageWrapper from 'client/components/PageWrapper';
import Terms from 'client/components/Terms';
import PrivacyPolicy from 'client/components/PrivacyPolicy';
import ProfileSettings from 'client/components/ProfileSettings';
import Callback from 'client/scenes/Callback';
import Utilities from 'client/scenes/Utilities';
import AuthenticatedRoute from 'client/components/AuthenticatedRoute';
import UnauthenticatedRoute from 'client/components/UnauthenticatedRoute';
import UserImpersonation from 'client/components/UserImpersonation';
import Impersonation from 'client/scenes/Impersonation/Impersonation';
import { NO_IDP, widgetTypes } from 'builder/util/constants';
import WidgetConnectorNew from 'builder/scenes/SiteBuilder/WidgetConnector';
import Feedback from 'shared/components/Feedback/Feedback';
import { processWidgetData } from '../../components/PageLayout/utils';
import PageLayout from '../../components/PageLayout';
import React, { useMemo } from 'react';
import SearchWidgetContainer from 'shared/widgets/Search/SearchResultsContainer';
import { Location } from 'history';

interface SiteRoutesProps {
  site: Site;
  pages: Array<Page>;
  user: User;
  domain: string;
  onUpdateConfig?: (
    config: any,
    widgetId: string,
    defaultWidget: boolean
  ) => void;
  initiateLogout?: () => void;
  setResetToFalse?: () => void;
}
const SiteRoutes: React.FC<SiteRoutesProps> = ({
  site,
  pages,
  user,
  domain,
  onUpdateConfig,
  initiateLogout,
}) => {
  const siteLogo = site.theme?.logo || ({ url: '', alt: '' } as Image);
  const siteRoutes = pages
    .filter(page => page.current)
    .map(page => {
      const RouteComponent =
        page.current.authentication && !(site.idpHint === NO_IDP)
          ? AuthenticatedRoute
          : UnauthenticatedRoute;
      return (
        <RouteComponent
          siteId={site.siteId}
          pageId={page.id}
          key={page.id}
          path={`/${page.current.slug}`}
          exact
          render={() => (
            <PageContext.Provider
              value={{
                site,
                pages,
                page,
              }}
            >
              <SitePage>
                <Helmet>
                  <title>
                    {page.current.title} | {site.name}
                  </title>
                </Helmet>
                <UserImpersonation clientId={site.clientId} user={user} />
                <SiteBody>
                  <WidgetConnectorNew
                    page={page}
                    widgetId={'root'}
                    currentPage={page.current}
                    siteId={site.siteId}
                    pageId={page.id}
                    site={site}
                  />
                  <Feedback
                    value={site.header.feedback}
                    siteId={site.siteId}
                    siteName={site.name}
                    user={user}
                  />
                </SiteBody>
              </SitePage>
            </PageContext.Provider>
          )}
        />
      );
    });

  const homepage: Page = pages
    .filter(page => page.current)
    .find(page => page.id === site.horizonhomepageId);
  const homepageSlug: string =
    (homepage && homepage.current.slug) ||
    (pages[0] && pages[0].current.slug) ||
    '/';
  const postLoginPage: Page = pages.find(
    page => page.id === site.horizonpostLoginPageId
  );
  const postLoginPageSlug: string =
    postLoginPage && postLoginPage.current && postLoginPage.current.slug;

  function getPageIdFromLocation(location: Location): number | undefined {
    const queryParams = new URLSearchParams(location.search);
    const pageId = queryParams.get('pageId');
    return pageId ? Number(pageId) : undefined;
  }

  const SearchResultsRouteView: React.FC<{
    location: Location;
  }> = ({ location }) => {
    const qpPageId = getPageIdFromLocation(location);
    const current =
      pages.find(p => p.id === qpPageId)?.current ||
      pages.find(p => p.current)?.current;

    const widgets = useMemo(
      () =>
        processWidgetData(
          current,
          onUpdateConfig,
          user,
          pages,
          site.siteId,
          site,
          initiateLogout,
          history
        ),
      [current, onUpdateConfig, user, pages, site.siteId, site, initiateLogout]
    );

    const searchWidgetState = useMemo(
      () => widgets.find(widget => widget.type === widgetTypes.Search),
      [widgets]
    );

    return (
      <PageLayout initialWidgets={widgets} currentPage={current} site={site}>
        <SearchWidgetContainer
          site={site}
          currentPage={current}
          searchWidgetState={searchWidgetState}
          pageId={qpPageId}
        />
      </PageLayout>
    );
  };

  return (
    <Switch>
      <Route
        path="/callback"
        render={({ history, location }) => (
          <Callback
            history={history}
            location={location}
            successRedirect={`/${postLoginPageSlug || homepageSlug}`}
            site={site}
            pages={pages}
            user={user}
          />
        )}
      />
      {siteRoutes}
      <Route
        path="/terms"
        render={() => (
          <PageWrapper
            title="Terms"
            header={site.header}
            logo={siteLogo}
            pages={pages}
            siteId={site.siteId}
            user={user}
            clientId={site.clientId}
          >
            <Terms />
            <SiteFooter footer={site.footer} logo={siteLogo} pages={pages} />
          </PageWrapper>
        )}
      />
      <Route
        path="/privacy"
        render={() => (
          <PageWrapper
            title="Privacy Policy"
            header={site.header}
            logo={siteLogo}
            pages={pages}
            siteId={site.siteId}
            user={user}
            clientId={site.clientId}
          >
            <PrivacyPolicy />
            <SiteFooter footer={site.footer} logo={siteLogo} pages={pages} />
          </PageWrapper>
        )}
      />
      <AuthenticatedRoute
        path="/profile"
        siteId={site.siteId}
        pageId={pages.filter(page => page.current).find(page => page).id}
        homepageSlug={homepageSlug}
        render={() => {
          const widgets = processWidgetData(
            pages.filter(page => page.current).find(page => page).current,
            onUpdateConfig,
            user,
            pages,
            site.siteId,
            site,
            initiateLogout,
            history
          );
          return (
            <PageLayout
              initialWidgets={widgets}
              currentPage={
                pages.filter(page => page.current).find(page => page).current
              }
              site={site}
            >
              <ProfileSettings domain={site.domain} />
            </PageLayout>
          );
        }}
      />

      <Route
        path="/sites/:siteId/search-results"
        render={({ location }) => (
          <SearchResultsRouteView location={location} />
        )}
      />
      <Route
        path="/__util__"
        render={() => {
          const widgets = processWidgetData(
            pages.filter(page => page.current).find(page => page).current,
            onUpdateConfig,
            user,
            pages,
            site.siteId,
            site,
            initiateLogout,
            history
          );
          return (
            <PageLayout
              initialWidgets={widgets}
              currentPage={
                pages.filter(page => page.current).find(page => page).current
              }
              site={site}
            >
              <Utilities domain={domain} />
            </PageLayout>
          );
        }}
      />
      <Route
        path="/impersonation"
        render={() => (
          <PageWrapper
            title="Impersonation"
            header={site.header}
            logo={siteLogo}
            pages={pages}
            siteId={site.siteId}
            user={user}
            clientId={site.clientId}
          >
            <Impersonation
              postImpersonationPage={postLoginPageSlug || homepageSlug}
            />
          </PageWrapper>
        )}
      />
      <Route>
        <Redirect to={`/${homepageSlug}`} />
      </Route>
    </Switch>
  );
};

export default SiteRoutes;
